<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { UsageProfile } from "@modules/usage-profile/types"

/** Nombre d'éléments par page (pagination) */
const PAGE_SIZE = 5

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            list_empty: "Aucun profil d'utilisation.",
            list_empty_search: 'Aucun résultat trouvé pour "{search}"',
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Liste des profils */
        profiles: UsageProfile[]
        /** Afficher le menu d'édition sur chaque profil */
        editable?: boolean
        /** Afficher un radio pour sélectionner */
        selectable?: boolean
        /** Liste scrollable (pas de pagination) */
        scrollable?: boolean
        /** Filtre de recherche (pour message vide) */
        search?: string
        /** Désactiver le message quand liste vide sans recherche */
        disabledEmptyMessage?: boolean
    }>(),
    {
        editable: false,
        selectable: false,
        scrollable: false,
        search: "",
        disabledEmptyMessage: false,
    }
)

const emit = defineEmits<{
    "click:profile": [profileId: string]
    "click:edit": [profileId: string]
    "click:remove": [profileId: string]
    "click:duplicate": [profileId: string]
    "edit:week": [profileId: string]
    "edit:exception": [profileId: string]
    "edit:buildings": [profileId: string]
}>()

// ================================
// STATE
// ================================
/** Index (0-based) de la première ligne affichée (lié au Paginator PrimeVue) */
const first = ref(0)

/** Profil sélectionné (pour radio) */
const selected = ref<string | null>(null)

/** Profil ouvert (accordéon) */
const profileOpened = ref<string | null>(null)

// ================================
// COMPUTED
// ================================
/** Profils paginés ou complets */
const getProfiles = computed(() => {
    if (props.scrollable) return props.profiles
    return props.profiles.slice(first.value, first.value + PAGE_SIZE)
})

// Réinitialise first si la liste raccourcit (ex. filtre) et que first serait hors limites
watch(
    () => props.profiles.length,
    (length) => {
        if (!props.scrollable && first.value >= length && length > 0) {
            first.value = 0
        }
    }
)

/** Message liste vide */
const getEmptyMessage = computed(() => {
    if (props.search) {
        return t("list_empty_search", {
            search: props.search,
        })
    }
    return t("list_empty")
})

// ================================
// INTERACTIONS
// ================================
/** Gère l'ouverture d'un profil (accordéon) */
function onProfileOpen(profileId: string): void {
    profileOpened.value = profileOpened.value === profileId ? null : profileId
}

/** Gère la sélection d'un profil (radio) */
function onSelectProfile(profileId: string): void {
    selected.value = profileId
    emit("click:profile", profileId)
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT -->
    <!-- ================================ -->
    <div
        class="usage-profile-list relative flex flex-col gap-2"
        :class="{
            'max-h-[19.25rem] overflow-y-auto': scrollable,
        }"
        :data-scrollable="scrollable"
        :data-selectable="selectable"
        :data-length="getProfiles.length"
    >
        <UsageProfileItem
            v-for="(profile, index) in getProfiles"
            :key="profile._id"
            class="usage-profile-list__item flex flex-col gap-4 border-b border-white transition-all duration-300 min-h-11"
            :data-not-last-child="index === getProfiles.length - 1"
            :data-open="profileOpened === profile._id"
            :profile="profile"
            :hide-menu="!editable"
            :selectable="selectable"
            :model-value="selected"
            radio-name="list-profile"
            hide-see-profiles
            hide-replace
            accordion="list-profile"
            @click:open="onProfileOpen(profile._id)"
            @update:model-value="onSelectProfile"
            @click:edit="emit('click:edit', profile._id)"
            @click:remove="emit('click:remove', profile._id)"
            @click:duplicate="emit('click:duplicate', profile._id)"
            @edit:week="emit('edit:week', profile._id)"
            @edit:exception="emit('edit:exception', profile._id)"
            @edit:buildings="emit('edit:buildings', profile._id)"
        />
        <p
            v-if="
                (getProfiles.length === 0 && !disabledEmptyMessage) ||
                (getProfiles.length === 0 && !!search)
            "
            class="flex items-center justify-center h-full text-sm text-surface-600"
        >
            {{ getEmptyMessage }}
        </p>
        <BasePaginator
            v-if="!scrollable && profiles.length > PAGE_SIZE"
            v-model:first="first"
            :rows="PAGE_SIZE"
            :total-records="profiles.length"
            :always-show="false"
            class="mt-8"
        />
    </div>
</template>
